import { isOfflineWriteBlockedError } from "common/utils/offlineWriteGuard";

const NETWORK_ERROR_PATTERNS = [
  "failed to fetch",
  "networkerror",
  "network request failed",
  "load failed",
  "aborted",
  "unable to resolve host",
  "failed to connect",
  "connection refused",
  "connection reset",
  "connection aborted",
  "connection timed out",
  "read timed out",
  "socket timeout",
  "timed out",
  "network is unreachable",
  "no route to host",
  "host unreachable",
  "connection closed",
  "software caused connection abort",
  "the internet connection appears to be offline",
  "the network connection was lost",
  "a server with the specified hostname could not be found",
  "could not connect to the server",
  "nsurlerrordomain",
];

const NETWORK_ERROR_CODES = new Set([
  "UnknownHostException",
  "ENOTFOUND",
  "ECONNRESET",
  "ECONNREFUSED",
  "ETIMEDOUT",
  "EHOSTUNREACH",
  "ENETUNREACH",
]);

const RETRYABLE_HTTP_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function numberValue(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || !/^\d+$/.test(value)) return undefined;
  return Number(value);
}

function errorProperty(error: unknown, property: string): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  return stringValue((error as Record<string, unknown>)[property]);
}

function numericErrorProperty(
  error: unknown,
  property: string
): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  return numberValue((error as Record<string, unknown>)[property]);
}

function responseObject(error: unknown): unknown {
  if (!error || typeof error !== "object") return undefined;
  return (error as Record<string, unknown>).response;
}

function httpStatus(error: unknown): number | undefined {
  return (
    numericErrorProperty(error, "status") ??
    numericErrorProperty(error, "statusCode") ??
    numericErrorProperty(responseObject(error), "status") ??
    numericErrorProperty(responseObject(error), "statusCode")
  );
}

function hasNetworkErrorPattern(value: string | undefined): boolean {
  if (!value) return false;
  const lowerValue = value.toLowerCase();
  return NETWORK_ERROR_PATTERNS.some((pattern) => lowerValue.includes(pattern));
}

/** True when a failed request likely means no usable network (not a server/business error). */
export function isNetworkRequestError(error: unknown): boolean {
  if (isOfflineWriteBlockedError(error)) return true;
  if (error instanceof DOMException && error.name === "AbortError") return true;
  const status = httpStatus(error);
  if (status !== undefined) return RETRYABLE_HTTP_STATUSES.has(status);
  return (
    hasNetworkErrorPattern(errorProperty(error, "message")) ||
    hasNetworkErrorPattern(errorProperty(error, "name")) ||
    NETWORK_ERROR_CODES.has(errorProperty(error, "code") ?? "")
  );
}
